import React, { createContext, useState, useContext, useEffect } from 'react';
import { AbilityBuilder, Ability } from '@casl/ability';
import { ability } from '@/ability';
import { useAuthContext } from '@/auth';

const AbilityContext = createContext();

const AbilityProvider = ({ children }) => {
  const { auth } = useAuthContext();
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!auth) return;
    const { can, rules } = new AbilityBuilder(Ability);
    const roles = auth.user.roles || [];
    const permissions = auth.user.permissions || [];

    // Allow all actions for super-admin or is_admin_access
    if (roles.includes('super-admin') || permissions.includes('is_admin_access')) {
      can('manage', 'all');
    }

    // Override 'manage' permissions to 'handle' specific actions only
    // This prevents CASL from treating 'manage' as a wildcard and unintentionally allowing all actions
    const normalizePermission = (perm) => {
      const [action, ...subjectParts] = perm.split(/[_-]/);
      const subject = subjectParts.join('-');
      const safeAction = action === 'manage' ? 'handle' : action;
      return [safeAction, subject];
    };

    permissions.forEach((perm) => {
      const [action, subject] = normalizePermission(perm);
      can(action, subject);
    });

    ability.update(rules);
    setLoading(false);
  }, [auth]);

  return (
    <AbilityContext.Provider value={{ user: auth?.user, ability, loading }}>
      {children}
    </AbilityContext.Provider>
  );
};

// Custom hook to access AbilityContext
// Usage: const { ability } = useAbility();
const useAbility = () => {
  const context = useContext(AbilityContext);
  if (!context) {
    throw new Error('useAbility must be used within an AbilityProvider');
  }
  return context;
};

export { AbilityProvider, useAbility };